import { UserIcon } from 'lucide-react'
import { type ReactNode, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SettingsLayout } from '@/components/settings/settings-layout.organism'
import type {
  SettingsNavGroup,
  SettingsNavItem,
} from '@/components/settings/settings-nav-item.interface'
import { orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

import { GeneralProfileSettingsView } from './general-profile-settings.view'
import {
  PROFILE_DEFAULT_VIEW,
  PROFILE_URL_PATH_TO_VIEW,
  PROFILE_VIEW_DESCRIPTIONS,
  PROFILE_VIEW_TITLES,
  PROFILE_VIEW_TO_URL_PATH,
  type ProfileViewType,
} from './profile-view.type'

const PROFILE_NAV_GROUPS: SettingsNavGroup[] = [
  {
    id: 'personal',
    label: 'Personal',
    itemList: [{ id: 'general', label: 'General', href: settingsRoutes.general, icon: UserIcon }],
  },
]

const VIEW_COMPONENTS: Partial<Record<ProfileViewType, React.FC>> = {
  general: GeneralProfileSettingsView,
}

const DefaultContent: React.FC<{ title: string }> = ({ title }) => (
  <div className="profile-settings-content">
    <p className="text-muted-foreground">Content for {title} will be added here.</p>
  </div>
)

export const ProfilePage = (): ReactNode => {
  const { profileView, orgSlug } = useParams<{ profileView?: string; orgSlug: string }>()
  const navigate = useNavigate()

  const viewType: ProfileViewType =
    PROFILE_URL_PATH_TO_VIEW[profileView ?? ''] ?? PROFILE_DEFAULT_VIEW

  useEffect(() => {
    if (!profileView && orgSlug) {
      navigate(
        `/${orgSlug}/${orgRoutes.profile}/${PROFILE_VIEW_TO_URL_PATH[PROFILE_DEFAULT_VIEW]}`,
        { replace: true }
      )
    }
  }, [profileView, orgSlug, navigate])

  const handleNavigate = useCallback(
    (item: SettingsNavItem): void => {
      if (!orgSlug) return
      navigate(`/${orgSlug}/${orgRoutes.profile}/${item.href}`)
    },
    [navigate, orgSlug]
  )

  const contentTitle = PROFILE_VIEW_TITLES[viewType]
  const ViewComponent = VIEW_COMPONENTS[viewType]

  return (
    <SettingsLayout
      groupList={PROFILE_NAV_GROUPS}
      activeItemId={viewType}
      onNavigate={handleNavigate}
      contentTitle={contentTitle}
      contentDescription={PROFILE_VIEW_DESCRIPTIONS[viewType]}
    >
      {ViewComponent ? <ViewComponent /> : <DefaultContent title={contentTitle} />}
    </SettingsLayout>
  )
}
